import React, {
    FormEvent, ReactElement, useEffect, useState,
} from "react";
import { Card } from "react-bootstrap";
import {
    AiOutlineClose, AiOutlineCloseCircle, AiOutlineEdit, AiOutlineUserAdd, AiTwotoneFilter,
} from "react-icons/ai";
import {
    BiAddToQueue, BiMessageAdd, BiNote, BiTask, BiTime,
} from "react-icons/bi";
import {
    BsAlarm, BsArchive, BsCode, BsFillArrowDownRightSquareFill,
    BsFillCalendar2MinusFill, BsLayoutThreeColumns, BsList, BsPersonCheckFill, BsTags,
} from "react-icons/bs";
import { FaRobot, FaUserPlus } from "react-icons/fa";
import { GoDiffIgnored } from "react-icons/go";
import { GrAction, GrActions } from "react-icons/gr";
import { HiOutlineBookOpen, HiShieldExclamation } from "react-icons/hi";
import { IoPersonSharp } from "react-icons/io5";
import {
    MdAvTimer,
    MdOutlineAirplaneTicket, MdOutlineFormatSize, MdOutlineTipsAndUpdates, MdTimerOff,
} from "react-icons/md";
import { RiEditBoxLine, RiInputCursorMove, RiTicket2Line } from "react-icons/ri";
import { TiArrowLoop } from "react-icons/ti";
import { VscDebugDisconnect } from "react-icons/vsc";
import { v4 as uuidv4 } from "uuid";
import {
    ActionContextType, FormField, FormFieldReplacementInput, useGetInstalledWorkflowServiceActionQuery,
    useGetInstalledWorkflowServiceActionsQuery,
    useGetInstalledWorkflowServicesQuery,
    useGetInstalledWorkflowSystemActionsQuery,
    WorkflowServiceAction, WorkflowServiceChildAction, WorkflowStep, WorkflowStepInput,
    WorkflowStepOutputParameter, WorkflowStepParameterInput,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import EditableControl, { ControlType } from "../editable-text/EditableText";
import FieldTypeValueDisplay from "../form-field/FieldTypeValueDisplay";
import Loading from "../loading/Loading";
import { ReplacementGroup } from "../replacement-group";
import { ActionProps } from "./actions/base";
import ConditionSettings from "./actions/condition";
import FormatSettings from "./actions/format";
import InHoursOfOperationSettings from "./actions/in-hours-of-operation";
import MapSettings from "./actions/map";
import SetTicketFieldValueSettings from "./actions/set-ticket-field-value";
import TriggerConnectorSettings from "./actions/trigger-connector";
import WebhookSettings from "./actions/webhook";
import Styles from "./Workflow.module.scss";
import ApprovalSettings from "./actions/approval";

enum CardPath {
    LIST,
    SERVICE_ACTION,
    CONFIGURE_ACTION,
}

export interface CardParams extends Record<CardPath, Record<string, string>> {
    [CardPath.SERVICE_ACTION]: {
        serviceId: string;
    },
    [CardPath.CONFIGURE_ACTION]: {
        actionId: string;
    };
}

const DEFAULT_TAG = "Popular";

const ICON_MAP: Record<string, ReactElement> = {
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,condition)": <AiTwotoneFilter />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-subtask)": <BiTask />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,assign-to-queue)": <BiAddToQueue />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,assign-to-user)": <AiOutlineUserAdd />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-finished)": <AiOutlineCloseCircle />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-hold)": <BsArchive />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-ignore)": <GoDiffIgnored />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-reopen)": <HiOutlineBookOpen />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-priority)": <MdOutlineAirplaneTicket />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-severity)": <RiTicket2Line />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-field-value)": <RiInputCursorMove />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map)": <BsList />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-message)": <BiMessageAdd />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-note)": <BiNote />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-participant)": <FaUserPlus />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger)": <BsFillArrowDownRightSquareFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger-connector)": <VscDebugDisconnect />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-field-value)": <RiEditBoxLine />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,delay-time)": <BsFillCalendar2MinusFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-sla-tier)": <HiShieldExclamation />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,loop)": <TiArrowLoop />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,format)": <MdOutlineFormatSize />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-status-updated)": <MdOutlineTipsAndUpdates />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook)": <BsCode />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-date-in-hours-of-operation)": <MdTimerOff />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-state)": <BsLayoutThreeColumns />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-field-updated)": <MdAvTimer />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-tags)": <BsTags />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-requestor)": <IoPersonSharp />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-user-in-group)": <BsPersonCheckFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-new-message)": <BiTime />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-due-date-expired)": <BsAlarm />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,run-deflection)": <FaRobot />,

};

const CONFIG_MAP: Record<string, (params: ActionProps) => ReactElement> = {
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,condition)": ConditionSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map)": MapSettings,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger-connector)": TriggerConnectorSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-field-value)": SetTicketFieldValueSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,format)": FormatSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook)": WebhookSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-date-in-hours-of-operation)": InHoursOfOperationSettings,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:8824f6a945534d91bfc0a99bb3ba07eb,send-approval)": ApprovalSettings,

};

const DEFAULT_ICON = <GrActions />;

type Navigate = <P extends CardPath>(card: P, params: CardParams[P]) => void;

function createId() {
    return uuidv4().replace(/-/g, "");
}

function applyTemplate(action: WorkflowServiceAction, parameters: WorkflowStepParameterInput[]): string {
    let name = action.name as string;
    if (action.displayTemplate?.template) {
        name = action.displayTemplate.template;
        action.displayTemplate.parameters?.forEach(param => {
            const parts = param.path?.split(".") ?? [];
            let replace = "";
            if (parts[0] === "parameters") {
                const fieldId = `urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,${parts[1]})`;
                replace = parameters
                    .find(row => row.value.fieldId === parts[1] || row.value.fieldId === fieldId)?.value
                    .displayValue ?? "";
            }
            name = name.replace(new RegExp(`{{${param.key}}}`, "g"), replace);
        });
    }
    return name;
}

function CardConfigureAction({
    onChange, params, onHide, values, input, cardName, onChangeCardName, workflow, id,
}: {
    onChange: (
        action: WorkflowStepParameterInput[],
        serviceAction: WorkflowServiceAction,
        output?: WorkflowStepOutputParameter[],
        allowedChildren?: WorkflowServiceChildAction[],
        id?: string
    ) => void;
    values: WorkflowStepParameterInput[];
    params: CardParams[CardPath.CONFIGURE_ACTION];
    onHide: () => void;
    input: ReplacementGroup[];
    cardName: string | undefined;
    onChangeCardName: (name: string) => void;
    workflow: WorkflowStep;
    id: string | undefined;
}): ReactElement {
    const { data: services, loading: isLoading } = useGetInstalledWorkflowServiceActionQuery({
        variables: {
            id: params.actionId,
        },
    });

    const action = services?.workflowServiceAction;
    useEffect(() => {
        onChange([], action as WorkflowServiceAction);
    }, [action?.input?.isEmpty]);

    if (isLoading || !action) {
        return <Loading />;
    }

    const valuesMap = values.toIdMap(field => field.value.fieldId) ?? {};

    const CustomConfig = CONFIG_MAP[params.actionId];

    return (

        <div className={services?.workflowServiceAction ? Styles["create-action-lg"] : ""}>
            <div className="d-flex justify-content-between align-items-start ">
                <h4 className="d-flex align-items-end">
                    <EditableControl
                        type={ControlType.TEXT}
                        isFormField
                        value={cardName ?? services?.workflowServiceAction?.name}
                        onChange={value => onChangeCardName(value as string)}
                    >
                        <div>
                            {cardName ?? services?.workflowServiceAction?.name}
                            <AiOutlineEdit className="text-light ms-2" size={20} />
                        </div>
                    </EditableControl>
                </h4>
                <button type="button" onClick={onHide} className="btn btn-none p-0 m-0 mt-n2">
                    <AiOutlineClose className="text-light" />
                </button>
            </div>

            <p>
                {services?.workflowServiceAction?.description}
            </p>

            {CustomConfig ? (
                <CustomConfig
                    service={action as WorkflowServiceAction}
                    input={input}
                    values={values}
                    workflow={workflow}
                    id={id}
                    onChange={value => {
                        onChange(value.values.map(row => ({
                            value: row,
                            replacement: value.replacements,
                        })), action as WorkflowServiceAction, value.output, value.allowedChildren, value.id);
                    }}
                />
            ) : (
                <div>
                    {action.input?.map(field => (
                        <FieldTypeValueDisplay
                            key={field.id}
                            className="mt-4 mx-2"
                            inputClassName="form-control my-2"
                            field={field as FormField}
                            value={valuesMap[field.id]?.value ?? field.defaultValue}
                            replacements={valuesMap[field.id]?.replacement as FormFieldReplacementInput[]}
                            replaceableInput={input}
                            getCurrentForm={() => Object.values(valuesMap).map(row => row.value)}
                            integrationId={services?.workflowServiceAction?.integrations?.firstElement?.id}
                            onChange={(changedValue, replacements) => {
                                const copy = Array.from(values);
                                const index = values.findIndex(row => row.value.fieldId === field.id);
                                if (index >= 0) {
                                    copy[index] = {
                                        value: changedValue,
                                        replacement: replacements,
                                    };
                                } else {
                                    copy.push({
                                        value: changedValue,
                                        replacement: replacements,
                                    });
                                }
                                onChange(copy, action as WorkflowServiceAction);
                            }}
                        />
                    ))}
                </div>
            )}

        </div>
    );
}

function CardServiceActions({
    navigate, params, onHide, onSelected,
}: {
    navigate: Navigate;
    params: CardParams[CardPath.SERVICE_ACTION];
    onHide: () => void;
    onSelected: (action: WorkflowServiceAction, serviceId: string) => void;
}): ReactElement {
    const { data: service, loading: isLoading } = useGetInstalledWorkflowServiceActionsQuery({
        variables: {
            id: params.serviceId,
        },
    });

    if (isLoading) {
        return <Loading />;
    }

    const isEmpty = !service?.workflowService?.actions || service?.workflowService?.actions?.isEmpty;
    return (

        <div className={!isEmpty ? Styles["create-action-lg"] : ""}>
            <div className="d-flex justify-content-between align-items-start ">
                <h4>
                    {service?.workflowService?.name}
                    {" "}
                    Actions
                </h4>
                <button type="button" onClick={onHide} className="btn btn-none p-0 m-0 mt-n2">
                    <AiOutlineClose className="text-light" />
                </button>
            </div>

            {isEmpty ? (
                <div className="text-center mt-3 text-gray-500">
                    Sorry it seems
                    {" "}
                    {service?.workflowService?.name}
                    {" "}
                    does not have any configured actions you can take.
                </div>
            ) : (
                <>
                    <p>
                        Below are the actions available for
                        {" "}
                        {service?.workflowService?.name}
                        . Please choose which action you would like to take in this part of the workflow.
                    </p>
                    <div className="list-group list-flush">
                        {service?.workflowService?.actions?.sortBy("name").map(action => (
                            <button
                                key={action.id}
                                className="btn btn-outline m-2 list-group-item text-start w-100"
                                type="button"
                                onClick={() => {
                                    onSelected(action, params.serviceId);
                                    navigate(CardPath.CONFIGURE_ACTION, {
                                        actionId: action.id,
                                    });
                                }}
                            >
                                <h5>
                                    {ICON_MAP[action.id as string] ?? DEFAULT_ICON}
                                    {" "}
                                    {action.name}
                                </h5>
                                <div className="text-wrap">
                                    {action.description}
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            )}

        </div>
    );
}

function CardListActions({
    navigate, onHide, onSelected, allowedActionIds, type,
}: {
    navigate: Navigate;
    onHide: () => void;
    onSelected: (action: WorkflowServiceAction) => void;
    allowedActionIds: string[] | undefined;
    type: ActionContextType;
}): ReactElement {
    const [selectedTag, setSelectedTag] = useState(DEFAULT_TAG);
    const [tagSearch, setTagSearch] = useState("");

    const { data: services, loading: isLoading } = useGetInstalledWorkflowServicesQuery({
        variables: {
            type,
        },
    });
    const { data: systemActionsData, loading: isActionsLoading } = useGetInstalledWorkflowSystemActionsQuery({
        variables: {
            type,
        },
    });

    let systemActions = systemActionsData?.workflowServiceSystemActions ?? [];
    if (allowedActionIds?.isNotEmpty) {
        const set = new Set(allowedActionIds);
        systemActions = systemActions.filter(row => set.has(row.id));
    }

    const tags = systemActions.flatMap(row => row.tags).distribution(row => row as string);

    const tagList = Object.entries(tags).sortByDescending(([tag, count]) => (tag === DEFAULT_TAG ? 100 : count));
    let displayActions = systemActions;
    if (tagSearch) {
        const search = tagSearch.toLowerCase();
        displayActions = displayActions.filter(row => row.name.toLocaleLowerCase().includes(search)
            || row.description.toLocaleLowerCase().includes(search));
    } else if (selectedTag) {
        displayActions = displayActions.filter(row => row.tags?.some(tag => tag === selectedTag));
    }
    return (

        <div className={services?.workflowServices?.isNotEmpty ? Styles["create-action-lg"] : ""}>
            <div className="d-flex justify-content-between align-items-start ">
                <h4>Add Automation Step</h4>
                <button type="button" onClick={onHide} className="btn btn-none p-0 m-0 mt-n2">
                    <AiOutlineClose className="text-light" />
                </button>
            </div>
            <p>
                Pick an action or service to start building your automation.
            </p>

            {isActionsLoading && <Loading />}
            <div className="d-flex align-items-stretch mt-2">
                <div className="col-2 me-3 border-end pe-3">
                    <input
                        onChange={evt => setTagSearch(evt.target.value)}
                        type="search"
                        placeholder="Search..."
                        value={tagSearch}
                        className="form-control-sm form-control fs-6 mt-1"
                        aria-label="Search for actions"
                    />
                    <div className=" list-group list-group-flush mt-3">
                        {tagList.map(([tag, count]) => (
                            <button
                                key={tag}
                                className={`list-group-item list-group-item-action border-start list-border rounded-0 p-0 mb-1
                                btn btn-none ps-2 ${!tagSearch && tag === selectedTag
                                ? " list-border-primary  fw-semibold"
                                : "text-gray-500"}`}
                                type="button"
                                onClick={() => {
                                    setSelectedTag(tag);
                                    setTagSearch("");
                                }}
                            >
                                {tag}
                                {" "}
                                (
                                {count}
                                )
                            </button>
                        ))}
                    </div>
                </div>
                <div className="col">
                    {displayActions.isEmpty && tagSearch && (
                        <div className="text-center mt-3 text-light">
                            Could not find any automation steps matching your search.
                        </div>
                    )}
                    <div className="list-group list-group-flush p-1">
                        {displayActions.sortBy("name").map(action => (
                            <button
                                key={action.id}
                                className="list-group-item text-start p-2 d-flex align-items-center"
                                type="button"
                                onClick={() => {
                                    onSelected(action);
                                    navigate(CardPath.CONFIGURE_ACTION, { actionId: action.id });
                                }}
                            >

                                <h6 className={`mb-0 col-3 ${Styles["list-icon"]}`}>
                                    {ICON_MAP[action.id as string] ?? DEFAULT_ICON}
                                    {" "}
                                    {action.name}

                                </h6>
                                <small className="ms-3 text-light">
                                    {action.description}
                                </small>

                            </button>
                        ))}

                    </div>
                </div>

            </div>

            {!allowedActionIds?.isNotEmpty && (
                <>
                    {isLoading && <Loading />}
                    {services?.workflowServices?.isEmpty ? (
                        <div className="mt-4 pt-3 text-light  ">
                            <h5 className="text-light">Services</h5>
                            It seems you do not have any service integrations configured. Service integrations
                            allow you to integrate with 3rd party services to automate your work.
                        </div>
                    ) : (
                        <div className="mt-4 pt-3 ">
                            <h5>Services</h5>
                            <p>
                                These are the services you have installed that you can take some action on. Make
                                services part of your workflow
                            </p>
                            <div className="d-flex align-items-stretch flex-wrap flex-columns">
                                {services?.workflowServices?.sortBy("name").map(service => (
                                    <div className="col-3 p-2" key={service.id}>
                                        <button
                                            className="btn p-0 w-100"
                                            type="button"
                                            onClick={() => navigate(CardPath.SERVICE_ACTION, { serviceId: service.id })}
                                        >
                                            <Card className="d-flex flex-column h-100 justify-content-center w-100">
                                                {service.iconUrl ? (
                                                    <Card.Img
                                                        src={service.iconUrl as string}
                                                        className={`p-4 m-auto ${Styles.icon}`}
                                                    />

                                                ) : (
                                                    <div className="p-4 text-center">
                                                        <GrAction size="60" />
                                                    </div>
                                                )}
                                                <Card.Body>
                                                    <Card.Title>{service.name}</Card.Title>
                                                    <Card.Text className="text-wrap text-gray-500">
                                                        {service.shortDescription?.createBlurbSingleLine(50)}
                                                    </Card.Text>
                                                </Card.Body>
                                            </Card>
                                        </button>
                                    </div>

                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

        </div>
    );
}

interface HistoryItem<T extends CardPath> {
    card: T;
    params: CardParams[T];
}

const HOME = {
    card: CardPath.LIST,
    params: {},
};

export interface WorkflowCardCreateProps {
    show: boolean;
    action: WorkflowStepInput | undefined;
    onHide: () => void;
    onSave: (action: WorkflowStep) => void;
    allowedActionIds?: string[];
    input: ReplacementGroup[];
    serviceAction?: WorkflowServiceAction;
    type: ActionContextType;
    workflow: WorkflowStep;
}

const EMPTY: WorkflowStepInput = {
    id: "",
    parameters: [],
    actionId: "",
    serviceId: "",
    name: "",
    description: "",
};

export default function WorkflowCardCreate({
    show, onHide, onSave, action, allowedActionIds, input, serviceAction, type, workflow,
}: WorkflowCardCreateProps): ReactElement {
    const [history, setHistory] = useState<HistoryItem<CardPath>[]>([HOME]);
    const [selectedServiceAction, setSelectedServiceAction] = useState<WorkflowServiceAction>();
    const [value, setValue] = useState<WorkflowStepInput>(EMPTY);
    const [cardName, setCardName] = useState<string>();
    const { card, params } = history.lastElement ?? HOME;

    useEffect(() => {
        if (show) {
            setHistory([HOME]);
            setValue(EMPTY);
            setCardName(action?.name);
            setSelectedServiceAction(action?.actionId);
        }
    }, [show]);

    useEffect(() => {
        if (serviceAction) {
            setSelectedServiceAction(serviceAction);
        }
    }, [serviceAction]);

    useEffect(() => {
        if (action) {
            setValue(action);
            setHistory([
                {
                    card: CardPath.CONFIGURE_ACTION,
                    params: {
                        actionId: action.actionId,
                    },
                },
            ]);
        }
    }, [action]);

    const navigate: Navigate = (cardPath, cardParams) => {
        setHistory(current => {
            const copy = Array.from(current);
            copy.push({
                card: cardPath,
                params: cardParams,
            });
            return copy;
        });
    };

    const goBack = () => {
        setHistory(current => {
            const copy = Array.from(current);
            copy.pop();
            return copy;
        });
    };

    const handleSave = (evt?: FormEvent<Element>) => {
        evt?.stopPropagation();
        const copy: WorkflowStep = {
            ...value,
            name: cardName ?? value.name,
            action: selectedServiceAction,
        };
        onSave(copy);
    };

    const handleSelected = (updated: WorkflowServiceAction, serviceId?: string) => {
        const workflowId = (workflow.id as string)?.match(/urn:fq:workflow:[a-zA-Z0-9]*/)?.[0]
            ?? "urn:fq:workflow:-1";
        setValue(current => ({
            ...current,
            id: current.id || `urn:fq:workflow-step:(${workflowId},${createId()})`,
            serviceId,
            name: updated.name,
            parameters: [],
            description: updated.description,
            actionId: updated.id,
            allowedChildren: updated.allowedChildren,
        }));
        setSelectedServiceAction(updated);
    };

    return (
        <Confirm
            shouldShow={show}
            onHide={history.length > 1 ? goBack : onHide}
            closeText={history.length > 1 ? "Back" : "Close"}
            size="xl"
            bodyClassName="p-4"
            confirmText={card === CardPath.CONFIGURE_ACTION ? "Save" : undefined}
            onConfirm={card === CardPath.CONFIGURE_ACTION ? handleSave : goBack}
        >
            <>
                {card === CardPath.LIST && (
                    <CardListActions
                        navigate={navigate}
                        onHide={onHide}
                        onSelected={handleSelected}
                        allowedActionIds={allowedActionIds}
                        type={type}
                    />
                )}
                {card === CardPath.SERVICE_ACTION && (
                    <CardServiceActions
                        navigate={navigate}
                        params={params as CardParams[CardPath.SERVICE_ACTION]}
                        onHide={onHide}
                        onSelected={handleSelected}
                    />
                )}
                {card === CardPath.CONFIGURE_ACTION && (
                    <CardConfigureAction
                        onChange={(values, selectedAction, output, allowedChildren, id) => {
                            if (!selectedAction) {
                                return;
                            }
                            if (selectedAction && values.isEmpty) {
                                setSelectedServiceAction(selectedAction);
                                return;
                            }
                            const name = applyTemplate(selectedAction, values);
                            const workflowId = (workflow.id as string)?.match(/urn:fq:workflow:[a-zA-Z0-9]*/)?.[0]
                                ?? "urn:fq:workflow:-1";
                            setValue(current => ({
                                ...current,
                                id: current.id || id || `urn:fq:workflow-step:(${workflowId},${createId()})`,
                                name,
                                parameters: values,
                                output,
                                allowedChildren,
                            }));
                            setSelectedServiceAction(selectedAction);
                        }}
                        id={value.id}
                        workflow={workflow}
                        cardName={cardName}
                        onChangeCardName={setCardName}
                        values={value.parameters as WorkflowStepParameterInput[]}
                        params={params as CardParams[CardPath.CONFIGURE_ACTION]}
                        onHide={onHide}
                        input={input}
                    />
                )}
            </>
        </Confirm>

    );
}

WorkflowCardCreate.defaultProps = {
    allowedActionIds: undefined,
    serviceAction: undefined,
};
